import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
// constants
import { Modals } from 'constants/index'
// hooks
import { useShowModal } from 'hooks'
// utils
import isEmpty from 'lodash/isEmpty'
// context
import { LinkingModeContext } from 'containers/Plan/Edit/layers/API/Linking'
import { LinkedItemsAPIContext } from 'containers/LinkedItems/layers/API'

type Props = React.PropsWithChildren<unknown>

interface IContext {
  handleObjectiveClick: (id: string) => void
  handleActionClick: (id: string) => void
  handleKPIClick: (id: string) => void
  handlePlanClick: (id: string, parentId: string, companyId: string) => void
  onUnlinkButtonClick: (id: string, query: any, variables: any) => () => void
  onLinkButtonClick: (source: { id: string; type: string; description: string; planId: string }) => void
  onMakeParentButtonClick: (id: string, parentPlanId: string, closeSearchBar: () => void) => void
}

export const LinkedItemsActionsContext = React.createContext<IContext>({} as any)

const LinkedItemsActions: React.FC<Props> = ({ children }) => {
  // hooks
  const { enableLinkingMode } = React.useContext(LinkingModeContext)

  const { deleteReferencedPlanItems, referencedPlanItems, unlinkPlanItem, updatePlan, updatePlanTree } =
    React.useContext(LinkedItemsAPIContext)

  const router = useRouter()

  // redux
  const { showModal: showObjectiveMinimizedModal, hideModal: hideObjectiveMinimizedModal } = useShowModal({
    modalType: Modals.OBJECTIVE_MINIMIZED_MODAL
  })

  const { showModal: showActionMinimizedModal, hideModal: hideActionMinimizedModal } = useShowModal({
    modalType: Modals.ACTION_MINIMIZED_MODAL
  })

  const { showModal: showKPIMinimizedModal, hideModal: hideKPIMinimizedModal } = useShowModal({
    modalType: Modals.KPI_MINIMIZED_MODAL
  })

  const { showModal: showConfirmChangeParentPlanModal } = useShowModal({
    modalType: Modals.CONFIRM_CHANGE_PARENT_PLAN_MODAL
  })

  // methods
  const setParentIdToRoute = (parentId: string) => {
    const { id, companyId, planViewMode } = router.query

    router.push(
      {
        pathname: `/plans/${companyId}/edit/${id}`,
        query: {
          parentId,
          planViewMode
        }
      },
      undefined,
      { shallow: true }
    )
  }

  const handleObjectiveClick: IContext['handleObjectiveClick'] = (id) => {
    showObjectiveMinimizedModal({ id })

    hideActionMinimizedModal()
    hideKPIMinimizedModal()
  }

  const handleActionClick: IContext['handleActionClick'] = (id) => {
    showActionMinimizedModal({ id })

    hideObjectiveMinimizedModal()
    hideKPIMinimizedModal()
  }

  const handleKPIClick: IContext['handleKPIClick'] = (id) => {
    showKPIMinimizedModal({ id })

    hideObjectiveMinimizedModal()
    hideActionMinimizedModal()
  }

  const handlePlanClick: IContext['handlePlanClick'] = async (id, parentId, companyId) => {
    await router.push({
      pathname: `/plans/${companyId}/edit/${id}`,
      query: { parentId }
    })
  }

  const onUnlinkButtonClick: IContext['onUnlinkButtonClick'] = (id, query, variables) => () =>
    unlinkPlanItem(id, query, variables)

  const onLinkButtonClick: IContext['onLinkButtonClick'] = (source) =>
    enableLinkingMode(source.id, source.planId, source.description, source.type)

  const onChangeParentPlan: IContext['onMakeParentButtonClick'] = (id, parentPlanId, closeSearchBar) => async () => {
    if (!isEmpty(referencedPlanItems)) {
      await updatePlanTree()
      await deleteReferencedPlanItems()
    }

    await updatePlan(id, parentPlanId)
    setParentIdToRoute(parentPlanId)
    closeSearchBar()
  }

  const onMakeParentButtonClick: IContext['onMakeParentButtonClick'] = (id, parentPlanId, closeSearchBar) => {
    if (router.query.parentId) {
      showConfirmChangeParentPlanModal({
        handleConfirm: onChangeParentPlan(id, parentPlanId, closeSearchBar)
      })
      return
    }

    updatePlan(id, parentPlanId)
    setParentIdToRoute(parentPlanId)
    closeSearchBar()
  }

  const value = {
    handleActionClick,
    handleKPIClick,
    handleObjectiveClick,
    handlePlanClick,
    onLinkButtonClick,
    onMakeParentButtonClick,
    onUnlinkButtonClick
  }

  return <LinkedItemsActionsContext.Provider value={value}>{children}</LinkedItemsActionsContext.Provider>
}

export default LinkedItemsActions
